import React from "react";
import Styles from "./FullLoadBooking.module.scss";
import {
    useFullLoadFeatures,
    useOlbHowItworkData,
    useFtlAchievement,
    useCustomerSaysData
} from "../service-data/ServiceData";

import { FaqsResponse } from "@vahak/core/dist/_services/faq.service";
import { ga4sendEvent } from "@vahak/core/dist/_services/user-analytics.service";

import Features from "../common/features/Features";
import Achievements from "../common/achievements/Achievements";
import HeroSection from "../common/hero-section/HeroSection";
import HowItWorks from "../common/how-it-works/HowItWorks";
import Faq from "../common/faq/Faq";
import { useTranslation } from "next-i18next";

import { GA4EventNames, GA4ServicePageFullTruckLoadEventValues } from "@vahak/core/dist/constants/event-names";
import { SOURCE_UTM } from "@vahak/core/dist/constants/misc";
import { PageTypes } from "@vahak/core/dist/constants/Pages";
import { ROUTES } from "@vahak/core/dist/constants/routeStrings";

import Intro from "../common/intro/Intro";
import OurCustomerSays from "../common/our-customer-says/OurCustomerSays";
import PageMetaTags from "../../common/page-meta-tags/PageMetaTags";
import { breadcrumbsConstants } from "../../../constants/breadcrumbs-constants";
import ServiceInformation from "../common/service-information/ServiceInformation";

interface FullLoadBookingProps {
    faqs: FaqsResponse[];
}

const metaData = {
    title: "Find Truck Load Booking - India's Largest Online Transport Marketplace",
    description:
        "Make the most of your truck's space by booking a full truck load on Vahak's online load booking marketplace and double your revenue. Get a free quote today!",
    keywords:
        "full truck loading, full truck load, full load, full truck load services, full load transport services, full truck load charges, full truck load logistics india, full truck load company, full truck load companies in india, full truck load logistics, full truck load transport",
    canonicalUrl: "https://www.vahak.in/full-truck-load",
    schemaOrgJsonUrl: "/full-truck-load",
    ogImgSrc: "https://d3ib4ot6oar1ep.cloudfront.net/1920/75/webp/driver_checking_truck_loads.png",
    ogImgAltText: "Book Full Truck Load",
    breadcrumbList: breadcrumbsConstants({}).fullTruckLoad
};

const FullLoadBooking = ({ faqs }: FullLoadBookingProps) => {
    const { t } = useTranslation("servicePages");

    return (
        <>
            <PageMetaTags
                title={metaData.title}
                description={metaData.description}
                keywords={metaData.keywords}
                canonicalUrl={metaData.canonicalUrl}
                ogUrl={metaData.canonicalUrl}
                ogImgSrc={metaData.ogImgSrc}
                ogImgAltText={metaData.ogImgAltText}
                schemaOrgJsonUrl={metaData.schemaOrgJsonUrl}
                breadcrumbList={metaData.breadcrumbList}
            />
            <main className={Styles.main}>
                <HeroSection
                    bannerTitle={t("servicePages:full-load-booking.hero-section.bannerTitle")}
                    bannerText={t("servicePages:full-load-booking.hero-section.bannerText")}
                    hrefURL={`${ROUTES.marketplace.getUrl("load")}?${SOURCE_UTM}=${PageTypes.FULL_TRUCK_LOAD}`}
                    hrefLabel={t("servicePages:full-load-booking.hero-section.hrefLabel")}
                    onLinkClick={() => {
                        ga4sendEvent(GA4EventNames.Servicepage_full_truck_load, {
                            value: GA4ServicePageFullTruckLoadEventValues.book_full_truck_load
                        });
                    }}
                    srcURL={`driver_checking_truck_loads.png`}
                    altText="driver checking truck loads"
                    sectionClass={Styles.herroSection}
                />

                <Achievements
                    achievementData={useFtlAchievement()}
                    sectionTitle={t("servicePages:online-load-booking.achievements.sectionTitle")}
                />

                <Intro text={t("servicePages:full-load-booking.intro.text")} />

                <ServiceInformation
                    heading={t("servicePages:full-load-booking.best-trucks-suited-for-full-truck-load.heading")}
                    sectionTitle={""}
                    sectionText={t("servicePages:full-load-booking.best-trucks-suited-for-full-truck-load.description")}
                />
                <ServiceInformation
                    heading={t("servicePages:full-load-booking.transport-services-included-in-full-truck-load.heading")}
                    sectionTitle={""}
                    sectionText={t(
                        "servicePages:full-load-booking.transport-services-included-in-full-truck-load.description"
                    )}
                />
                <ServiceInformation
                    heading={t("servicePages:full-load-booking.full-truck-load-service-providers-in-india.heading")}
                    sectionTitle={""}
                    sectionText={t(
                        "servicePages:full-load-booking.full-truck-load-service-providers-in-india.description"
                    )}
                />

                <Features
                    features={useFullLoadFeatures()}
                    sectionTitle={t("servicePages:full-load-booking.features.sectionTitle")}
                    ctaLink={`${ROUTES.marketplace.getUrl("load")}`}
                    ctaLabel={t("servicePages:full-load-booking.features.ctaLabel")}
                    onCtaClick={() => {
                        ga4sendEvent(GA4EventNames.Servicepage_full_truck_load, {
                            value: GA4ServicePageFullTruckLoadEventValues.explore_ftl_market
                        });
                    }}
                />
                <HowItWorks
                    howitwork={useOlbHowItworkData()}
                    sectionTitle={t("servicePages:full-load-booking.how-it-works.section-title")}
                    sectionText={t("servicePages:full-load-booking.how-it-works.section-text")}
                    tabAppNav={t("servicePages:online-load-booking.HowItWorks.tabAppNav")}
                    tabWebNav={t("servicePages:online-load-booking.HowItWorks.tabWebNav")}
                    onAppTabChange={() => {
                        ga4sendEvent(GA4EventNames.Servicepage_full_truck_load, {
                            value: GA4ServicePageFullTruckLoadEventValues.mobile_app
                        });
                    }}
                    onWebTabChange={() => {
                        ga4sendEvent(GA4EventNames.Servicepage_full_truck_load, {
                            value: GA4ServicePageFullTruckLoadEventValues.web
                        });
                    }}
                />
                <Faq
                    faqs={faqs}
                    sectionTitle={t("servicePages:full-load-booking.Faq.sectionTitle")}
                    sectionText={t("servicePages:full-load-booking.Faq.sectionText")}
                />

                <OurCustomerSays
                    customerSaysData={useCustomerSaysData()}
                    sectionTitle={t("servicePages:logistics-services.ourCustomerSays.sectionTitle")}
                    sectionText={t("servicePages:logistics-services.ourCustomerSays.sectionText")}
                />
            </main>
        </>
    );
};

export default FullLoadBooking;
